import { List } from 'immutable';
import { Contexts } from './context-records';
import { ExpressionTypes, FunctionCall } from './expression-records';
import { ContextViolation, isTypeViolation, ViolationLevels } from './violation-records';
const contextWithViolations = (context, ...violations) => ({
  context,
  violations: List(violations)
});
const checkIdentifierExpressionContext = (expression, dependencies) => {
  const {
    scope
  } = dependencies;
  const scopedValue = scope.get(expression.name);
  if (!scopedValue) {
    return contextWithViolations(Contexts.UNDEFINED);
  }
  return contextWithViolations(Contexts.CONSTANT);
};
const checkFieldExpressionContext = (expression, dependencies) => {
  const {
    fields
  } = dependencies;
  const referencedField = fields.get(expression.name);
  if (!referencedField || !referencedField.valid) {
    return contextWithViolations(Contexts.UNDEFINED);
  }
  return contextWithViolations(referencedField.context);
};
const checkPropertyExpressionContext = (expression, dependencies) => {
  const {
    properties
  } = dependencies;
  const property = properties.getIn([expression.table, expression.name]);
  if (!property) {
    return contextWithViolations(Contexts.UNDEFINED);
  }
  return contextWithViolations(Contexts.ROW_LEVEL);
};
const checkFunctionCallExpressionContext = (expression, dependencies) => {
  const checkedArguments = expression.arguments.map(e =>
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  checkExpressionContext(e, dependencies));
  const providedContext = checkedArguments.map(({
    context
  }) => context);
  const providedViolations = checkedArguments.map(({
    violations
  }) => violations).flatten(1);
  const {
    functions,
    operators
  } = dependencies;
  const functionType = functions[expression.name] || operators[expression.name];
  if (!functionType) {
    return contextWithViolations(Contexts.UNDEFINED, ...providedViolations.toArray());
  }
  const hasUndefinedContext = providedContext.includes(Contexts.UNDEFINED);
  const hasRowLevelContext = providedContext.includes(Contexts.ROW_LEVEL);
  const hasAggregateContext = providedContext.includes(Contexts.AGGREGATE);
  if (hasAggregateContext && hasRowLevelContext) {
    const contextViolation = ContextViolation({
      level: ViolationLevels.ERROR,
      expression,
      location: expression.location
    });
    return contextWithViolations(Contexts.UNDEFINED, contextViolation, ...providedViolations.toArray());
  }
  if (hasUndefinedContext) {
    return contextWithViolations(Contexts.UNDEFINED);
  }
  if (functionType.isAggregation) {
    return contextWithViolations(Contexts.AGGREGATE, ...providedViolations.toArray());
  }
  if (hasAggregateContext) {
    return contextWithViolations(Contexts.AGGREGATE, ...providedViolations.toArray());
  }
  if (hasRowLevelContext) {
    return contextWithViolations(Contexts.ROW_LEVEL, ...providedViolations.toArray());
  }
  return contextWithViolations(Contexts.CONSTANT, ...providedViolations.toArray());
};
const checkAggregationContextExpressionContext = (expression, dependencies) => {
  const {
    dimensions,
    expression: exp
  } = expression;
  const checkedDimensions = dimensions.map(e =>
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  checkExpressionContext(e, dependencies));
  const providedViolations = checkedDimensions.map(({
    violations
  }) => violations).flatten(1);
  const {
    context,
    violations
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
  } = checkExpressionContext(exp, dependencies);
  if (context !== Contexts.UNDEFINED && context !== Contexts.AGGREGATE) {
    const contextViolation = ContextViolation({
      level: ViolationLevels.ERROR,
      expression,
      location: expression.location
    });
    return contextWithViolations(Contexts.ROW_LEVEL, contextViolation, ...providedViolations.toArray(), ...violations.toArray());
  }
  return contextWithViolations(Contexts.ROW_LEVEL, ...providedViolations.toArray(), ...violations.toArray());
};
function checkContextWithRemappedExpression(mapToFunctionCallExpression) {
  return (expression, dependencies) => {
    const functionExpression = mapToFunctionCallExpression(expression);
    const {
      context,
      violations
    } = checkFunctionCallExpressionContext(functionExpression, dependencies);

    // @ts-expect-error add annotations
    const remapExpression = e => functionExpression.equals(e) ? expression : e;
    function remapViolation(violation) {
      if (!isTypeViolation(violation)) {
        return violation;
      }
      return violation.update('expression', remapExpression).update('parentExpression', remapExpression);
    }
    const remappedViolations = violations.map(remapViolation);
    return contextWithViolations(context, ...remappedViolations.toArray());
  };
}
const checkUnaryExpressionContext = checkContextWithRemappedExpression(expression => FunctionCall({
  type: ExpressionTypes.FUNCTION_CALL,
  name: expression.type,
  arguments: [expression.argument],
  location: expression.location
}));
const checkBinaryExpressionContext = checkContextWithRemappedExpression(expression => FunctionCall({
  type: ExpressionTypes.FUNCTION_CALL,
  name: expression.type,
  arguments: [expression.left, expression.right],
  location: expression.location
}));
export const checkExpressionContext = (expression, dependencies) => {
  if (!expression) {
    return contextWithViolations(Contexts.UNDEFINED);
  }
  switch (expression.type) {
    // Literal expressions
    case ExpressionTypes.NULL_LITERAL:
    case ExpressionTypes.NUMERIC_LITERAL:
    case ExpressionTypes.STRING_LITERAL:
    case ExpressionTypes.BOOLEAN_LITERAL:
      return contextWithViolations(Contexts.CONSTANT);

    // Reference expressions
    case ExpressionTypes.IDENTIFIER:
      return checkIdentifierExpressionContext(expression, dependencies);
    case ExpressionTypes.FIELD:
      return checkFieldExpressionContext(expression, dependencies);
    case ExpressionTypes.PROPERTY:
      return checkPropertyExpressionContext(expression, dependencies);

    // Function expressions
    case ExpressionTypes.FUNCTION_CALL:
      return checkFunctionCallExpressionContext(expression, dependencies);

    // Context expressions
    case ExpressionTypes.AGGREGATION_CONTEXT:
      return checkAggregationContextExpressionContext(expression, dependencies);

    // Unary operator expressions
    case ExpressionTypes.NOT:
    case ExpressionTypes.POSITIVE:
    case ExpressionTypes.NEGATIVE:
      return checkUnaryExpressionContext(expression, dependencies);

    // Binary operator expression
    case ExpressionTypes.AND:
    case ExpressionTypes.OR:
    case ExpressionTypes.ADD:
    case ExpressionTypes.SUBTRACT:
    case ExpressionTypes.MULTIPLY:
    case ExpressionTypes.DIVIDE:
    case ExpressionTypes.MODULUS:
    case ExpressionTypes.GREATER:
    case ExpressionTypes.GREATER_OR_EQUAL:
    case ExpressionTypes.LESS:
    case ExpressionTypes.LESS_OR_EQUAL:
    case ExpressionTypes.EQUAL:
    case ExpressionTypes.NOT_EQUAL:
      return checkBinaryExpressionContext(expression, dependencies);
    default:
      throw new Error('Unhandled expression context');
  }
};